import { useState } from 'react';
import { HORAS_PARA_CANCELAR_SIN_CARGO, PORCENTAJE_SENA_SUGERIDO, type Catalogos, type MiPerfilProfesional } from '@medchoice/shared';
import { ApiError } from '../../api/client';
import { consultorio } from '../../api/consultorio';
import { publico } from '../../api/publico';
import { usePedido } from '../../api/usePedido';
import { soloDigitos, validarTelefono } from '../../forms/validacion';
import {
  AreaTexto, AvisoError, Boton, Cargando, Casilla, Entrada, FalloDeCarga, Interruptor, Selector, Tarjeta,
  useAvisos,
} from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { pesos } from '../../ui/format';
import { AvisoDeVerificacion, Seccion } from './piezas';

export function Perfil() {
  const perfil = usePedido(() => consultorio.perfil(), []);
  const catalogos = usePedido(() => publico.catalogos(), []);
  return (
    <SeccionPanel titulo="Mi perfil" descripcion="Esto es lo que ve el paciente antes de reservar">
      {(perfil.estado === 'cargando' || catalogos.estado === 'cargando') && <Cargando />}
      {perfil.estado === 'error' && <FalloDeCarga mensaje={perfil.error ?? ''} alReintentar={perfil.recargar} />}
      {perfil.datos && catalogos.datos && <Formulario perfil={perfil.datos} catalogos={catalogos.datos} />}
    </SeccionPanel>
  );
}

const alternar = (lista: string[], valor: string) =>
  lista.includes(valor) ? lista.filter((v) => v !== valor) : [...lista, valor];

function Formulario({ perfil, catalogos }: { perfil: MiPerfilProfesional; catalogos: Catalogos }) {
  const { mostrarAviso } = useAvisos();
  const [telefono, setTelefono] = useState(perfil.telefono);
  const [anios, setAnios] = useState(perfil.anios_experiencia === null ? '' : String(perfil.anios_experiencia));
  const [especialidad, setEspecialidad] = useState(perfil.especialidades[0] ?? '');
  const [direccion, setDireccion] = useState(perfil.direccion ?? '');
  const [piso, setPiso] = useState(perfil.piso_depto ?? '');
  const [barrio, setBarrio] = useState(perfil.barrio ?? '');
  const [bio, setBio] = useState(perfil.bio ?? '');
  const [obras, setObras] = useState(perfil.obras_sociales);
  const [cobraSena, setCobraSena] = useState(perfil.cobra_sena);
  const [sena, setSena] = useState(perfil.monto_sena ? String(perfil.monto_sena / 100) : '');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const errorTelefono = validarTelefono(telefono);
  const errorSena = cobraSena && !(Number(sena) >= 1000) ? 'La seña mínima es de $1.000' : null;

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await consultorio.actualizarPerfil({
        telefono, bio: bio.trim() || null, anios_experiencia: anios === '' ? null : Number(anios),
        direccion: direccion.trim() || null, piso_depto: piso.trim() || null, barrio: barrio || null,
        // Solo se edita la principal: las demás especialidades se conservan.
        especialidades: especialidad && especialidad !== perfil.especialidades[0]
          ? [especialidad, ...perfil.especialidades.slice(1).filter((e) => e !== especialidad)]
          : undefined,
        obras_sociales: obras,
        cobra_sena: cobraSena, monto_sena: cobraSena ? Math.round(Number(sena) * 100) : perfil.monto_sena,
      });
      mostrarAviso({ tono: 'exito', titulo: 'Perfil actualizado', cuerpo: 'Los pacientes ya ven los cambios.' });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos guardar el perfil. Probá de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="pila config">
      <AvisoDeVerificacion />
      <Tarjeta>
        <Seccion titulo="Datos personales">
          <div className="formulario">
            <div className="formulario__fila">
              <Entrada etiqueta="Nombre y apellido" value={`${perfil.nombre} ${perfil.apellido}`} disabled />
              <Selector etiqueta="Especialidad" marcador="Elegí una" value={especialidad} opciones={catalogos.especialidades}
                onChange={(e) => setEspecialidad(e.target.value)} />
            </div>
            <div className="formulario__fila">
              <Entrada etiqueta="Matrícula" value={`${perfil.nro_matricula} · ${perfil.jurisdiccion_matricula}`} disabled
                ayuda="La matrícula la cambia la administración." />
              <Entrada etiqueta="Teléfono" inputMode="tel" value={telefono} error={errorTelefono ?? undefined}
                onChange={(e) => setTelefono(soloDigitos(e.target.value, 15))} />
            </div>
            <Entrada etiqueta="Años de experiencia" inputMode="numeric" value={anios}
              onChange={(e) => setAnios(soloDigitos(e.target.value, 2))} />
          </div>
        </Seccion>
      </Tarjeta>

      <Tarjeta>
        <Seccion titulo="Consultorio" descripcion="Dónde te atienden los pacientes.">
          <div className="formulario">
            <div className="formulario__fila">
              <Entrada etiqueta="Dirección" placeholder="Av. Cabildo 2340" maxLength={150} value={direccion}
                onChange={(e) => setDireccion(e.target.value)} />
              <Entrada etiqueta="Piso y depto." placeholder="3° B" maxLength={20} value={piso} onChange={(e) => setPiso(e.target.value)} />
            </div>
            <Selector etiqueta="Barrio" marcador="Elegí uno" value={barrio} opciones={catalogos.barrios}
              onChange={(e) => setBarrio(e.target.value)} />
          </div>
        </Seccion>
      </Tarjeta>

      <Tarjeta>
        <Seccion titulo="Presentación" descripcion="Contale al paciente cómo trabajás. Es lo primero que lee en tu perfil.">
          <AreaTexto aria-label="Presentación" rows={5} maxLength={600} value={bio}
            placeholder="Dermatóloga clínica y estética. Egresada de la UBA…" onChange={(e) => setBio(e.target.value)} />
        </Seccion>
      </Tarjeta>

      <Tarjeta>
        <Seccion titulo="Obras sociales" descripcion="Marcá las que aceptás. Los pacientes filtran por esto.">
          <div className="casillas">
            {catalogos.obras_sociales.map((o) => (
              <Casilla key={o} etiqueta={o} checked={obras.includes(o)} onChange={() => setObras(alternar(obras, o))} />
            ))}
          </div>
        </Seccion>
      </Tarjeta>

      <Tarjeta>
        <Seccion titulo="Seña" descripcion={`Pedir seña al reservar es lo que más baja las inasistencias. Si el paciente cancela con ${HORAS_PARA_CANCELAR_SIN_CARGO} horas o más, se le devuelve.`}>
          <div className="formulario">
            <Interruptor etiqueta="Pedir seña al reservar" checked={cobraSena} onChange={(e) => setCobraSena(e.target.checked)} />
            {cobraSena && (
              <Entrada etiqueta="Monto de la seña" inputMode="numeric" sufijo="pesos" value={sena} error={errorSena ?? undefined}
                ayuda={perfil.monto_sena ? `Hoy cobrás ${pesos(perfil.monto_sena)}.` : `Solemos sugerir un ${PORCENTAJE_SENA_SUGERIDO * 100} % de la consulta.`}
                onChange={(e) => setSena(soloDigitos(e.target.value, 7))} />
            )}
          </div>
        </Seccion>
      </Tarjeta>

      <AvisoError>{error}</AvisoError>
      <div className="acciones-pie">
        <Boton disabled={Boolean(errorTelefono || errorSena)} cargando={guardando} onClick={() => void guardar()}>Guardar cambios</Boton>
      </div>
    </div>
  );
}
